/**
 * CORS-fallback loader for HLS.js.
 *
 * M4 implementation — real loader, not a placeholder.
 *
 * Strategy per request:
 *   1. Try the upstream URL directly (CORS-friendly CDNs return here
 *      without any extra hop).
 *   2. On any failure (CORS preflight rejection, network error, or
 *      non-2xx HTTP status) retry through `/proxy?url=<encoded>` so
 *      our Cloudflare Pages Function can fetch and re-emit the
 *      response with permissive CORS headers.
 *   3. For manifest responses (m3u8/m3u) rewrite all relative URLs
 *      to absolute URLs routed through the proxy. This handles
 *      variant playlists, media segments, fMP4 init segments
 *      (`#EXT-X-MAP:URI="..."`), and audio tracks
 *      (`#EXT-X-MEDIA:URI="..."`).
 *
 * Why client-side rewriting:
 *   The proxy is a dumb pass-through. The browser's HLS.js will
 *   resolve relative segment URLs in the manifest against whatever
 *   URL the manifest was loaded from. If we serve a rewritten
 *   manifest from the proxy URL itself, the relative URLs would
 *   resolve against our origin, breaking the upstream. By rewriting
 *   the manifest body here to absolute proxy URLs, the browser
 *   ends up requesting `https://streampitch.com/proxy?url=...`
 *   for every segment — exactly what we want.
 */

import {
  type Loader,
  type LoaderCallbacks,
  type LoaderConfiguration,
  type LoaderContext,
  type LoaderResponse,
  type LoaderStats,
  type HlsProgressivePerformanceTiming,
  type HlsPerformanceTiming,
} from 'hls.js';

function emptyTiming(): HlsProgressivePerformanceTiming {
  return { start: 0, first: 0, end: 0 };
}

function emptyParsing(): HlsPerformanceTiming {
  return { start: 0, end: 0 };
}

function createStats(): LoaderStats {
  return {
    aborted: false,
    loaded: 0,
    retry: 0,
    total: 0,
    chunkCount: 0,
    bwEstimate: 0,
    loading: emptyTiming(),
    parsing: emptyParsing(),
    buffering: emptyTiming(),
  };
}

/**
 * Resolve every URL in an m3u8 manifest against the manifest's base
 * URL, then rewrite it to go through the proxy.
 *
 * Handles:
 *   • Plain URL lines (segments, variant playlists)
 *   • URI="..." inside tag lines (#EXT-X-MAP, #EXT-X-MEDIA, #EXT-X-KEY)
 *
 * Strips query strings from upstream base when computing the proxy
 * origin, since the proxy path lives on our own origin.
 */
export function rewriteManifest(
  text: string,
  manifestUrl: string,
  proxyBase: string
): string {
  let baseUrl: URL;
  try {
    baseUrl = new URL(manifestUrl);
  } catch {
    return text;
  }

  const proxify = (uri: string): string => {
    let absolute: string;
    try {
      absolute = new URL(uri, baseUrl).toString();
    } catch {
      return uri;
    }
    return `${proxyBase}?url=${encodeURIComponent(absolute)}`;
  };

  return text
    .split(/\r?\n/)
    .map((line) => {
      // Rewrite URI="..." inside tag lines.
      const withUri = line.replace(/URI="([^"]+)"/g, (_match, uri: string) => {
        return `URI="${proxify(uri)}"`;
      });

      const trimmed = withUri.trim();
      // Skip empty / comment / tag lines.
      if (!trimmed || trimmed.startsWith('#')) return withUri;
      // Treat as a URL (segment or variant playlist).
      return proxify(trimmed);
    })
    .join('\n');
}

type LoadResult =
  | { ok: true; proxied: boolean; response: Response; body: ArrayBuffer | string }
  | { ok: false; code: number; text: string };

async function tryDirect(
  url: string,
  headers: Record<string, string>,
  signal: AbortSignal
): Promise<LoadResult> {
  try {
    const response = await fetch(url, { headers, signal, mode: 'cors', credentials: 'same-origin' });
    if (response.ok || response.status === 206) {
      return { ok: true, proxied: false, response, body: await readBody(response) };
    }
    return { ok: false, code: response.status, text: response.statusText || 'Bad response' };
  } catch (err) {
    // fetch() throws TypeError on CORS / network failures.
    const msg = err instanceof Error ? err.message : String(err);
    return { ok: false, code: 0, text: `Direct fetch failed: ${msg}` };
  }
}

async function tryProxied(
  url: string,
  headers: Record<string, string>,
  signal: AbortSignal
): Promise<LoadResult> {
  const proxiedUrl = `/proxy?url=${encodeURIComponent(url)}`;
  try {
    const response = await fetch(proxiedUrl, { headers, signal });
    if (response.ok || response.status === 206) {
      return { ok: true, proxied: true, response, body: await readBody(response) };
    }
    return { ok: false, code: response.status, text: response.statusText || 'Proxy error' };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    return { ok: false, code: 0, text: `Proxy fetch failed: ${msg}` };
  }
}

async function readBody(response: Response): Promise<ArrayBuffer | string> {
  // We don't know the response type ahead of time. Use arrayBuffer
  // and decode later if we need text — simpler than two branches.
  return response.arrayBuffer();
}

export class CorsFallbackLoader implements Loader<LoaderContext> {
  public context: LoaderContext | null = null;
  public stats: LoaderStats = createStats();

  private controller: AbortController | null = null;
  private response: Response | null = null;

  constructor(_config?: unknown) {
    // HLS.js passes its full config; we don't need anything from it.
    void _config;
  }

  destroy(): void {
    this.abort();
    this.context = null;
    this.response = null;
  }

  abort(): void {
    this.stats.aborted = true;
    this.controller?.abort();
  }

  load(
    context: LoaderContext,
    _config: LoaderConfiguration,
    callbacks: LoaderCallbacks<LoaderContext>
  ): void {
    this.context = context;
    this.controller = new AbortController();
    this.stats = createStats();
    this.stats.loading.start = performance.now();

    const headers: Record<string, string> = { ...(context.headers || {}) };
    if (context.rangeEnd !== undefined && context.rangeStart !== undefined) {
      headers['Range'] = `bytes=${context.rangeStart}-${context.rangeEnd - 1}`;
    }

    const signal = this.controller.signal;
    const responseType = context.responseType;

    this.attempt(context.url, headers, signal)
      .then((result) => {
        if (this.stats.aborted) return;
        if (!result.ok) {
          callbacks.onError(
            { code: result.code, text: result.text },
            context,
            null,
            this.stats
          );
          return;
        }

        this.response = result.response;
        this.stats.loading.first = Math.max(performance.now(), this.stats.loading.start);
        this.stats.loading.end = performance.now();

        // HLS.js sets responseType='text' for every playlist/manifest
        // load (master, level, audio track, subtitle) and 'arraybuffer'
        // for segment/key loads. We trust that signal rather than the
        // upstream's Content-Type, which wrangler/dev or some CDNs
        // may mangle.
        let data: string | ArrayBuffer;
        if (responseType === 'text') {
          const text = new TextDecoder().decode(result.body as ArrayBuffer);
          const trimmed = text.trimStart();
          if (!trimmed) {
            callbacks.onError(
              { code: 0, text: 'Server returned an empty response for the stream manifest.' },
              context,
              null,
              this.stats,
            );
            return;
          }
          if (trimmed.startsWith('#EXTM3U')) {
            if (result.proxied) {
              // Manifest was loaded through the proxy (CORS/network
              // failed) — rewrite relative URLs to absolute proxy
              // URLs so every segment fetch also goes through the
              // proxy, keeping the CORS-fallback chain alive.
              const proxyBase = `${location.origin}/proxy`;
              data = rewriteManifest(text, context.url, proxyBase);
            } else {
              // Manifest loaded directly (CORS worked) — keep the
              // original URLs. Segments will be fetched directly from
              // the upstream, avoiding unnecessary proxy overhead and
              // potential upstream header requirements.
              data = text;
            }
          } else if (/^\s*<[!?]?html/i.test(trimmed) || /<\s*html/i.test(trimmed)) {
            // Upstream or proxy returned an HTML page instead of a manifest
            // (e.g. "channel offline" page, login page, or CDN error page
            // sent with HTTP 200). Reject immediately with a descriptive
            // error so HLS.js surfaces it as a network error rather than
            // a confusing manifestParsingError.
            callbacks.onError(
              {
                code: 0,
                text:
                  'Server returned HTML instead of a stream manifest. The stream may be offline, ' +
                  'blocked, or the URL has expired.',
              },
              context,
              null,
              this.stats,
            );
            return;
          } else {
            // Text but not a manifest (e.g. WebVTT subtitle) — pass through.
            data = text;
          }
          this.stats.loaded = this.stats.total = (data as string).length;
        } else {
          data = result.body as ArrayBuffer;
          this.stats.loaded = this.stats.total = (result.body as ArrayBuffer).byteLength;
        }

        const loaderResponse: LoaderResponse = {
          // Hand HLS.js the ORIGINAL upstream URL, not the proxy URL.
          // If we hand back the proxy URL, HLS.js uses it as the
          // manifest's base for resolving relative segment URLs —
          // which is exactly what we just fixed.
          url: context.url,
          data,
          code: result.response.status,
        };

        callbacks.onSuccess(loaderResponse, this.stats, context, result.response);
      })
      .catch((err: unknown) => {
        if (this.stats.aborted) return;
        const message = err instanceof Error ? err.message : String(err);
        callbacks.onError({ code: 0, text: message }, context, null, this.stats);
      });
  }

  private async attempt(
    url: string,
    headers: Record<string, string>,
    signal: AbortSignal
  ): Promise<LoadResult> {
    // Pass 1 — direct
    const direct = await tryDirect(url, headers, signal);
    if (direct.ok) return direct;
    if (signal.aborted || this.stats.aborted) {
      return { ok: false, code: 0, text: 'Aborted' };
    }
    // Pass 2 — through the Pages Function proxy
    return tryProxied(url, headers, signal);
  }

  getCacheAge(): number | null {
    if (!this.response) return null;
    const age = this.response.headers.get('age');
    return age ? parseFloat(age) : null;
  }

  getResponseHeader(name: string): string | null {
    return this.response ? this.response.headers.get(name) : null;
  }
}

/**
 * Returns the CorsFallbackLoader class itself, which is what HLS.js's
 * `loader` config option expects.
 */
export function corsFallbackLoaderClass() {
  return CorsFallbackLoader;
}
